'use client';

import { MouseEvent, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useEvent, useUnit } from 'effector-react';
import { trackChatOpen } from '@nx-frontend/features/ai-helper';
import { calculateContextMenuPosition, openContextMenuEvent } from '@nx-frontend/shared/ui/context-menu';
import { closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { getMoveCopyMenuItem } from '@nx-frontend/entities/move-copy-menu';
import { showCopyAccessDeniedPopup, useUnshareNote } from '@nx-frontend/shared/actions';
import { upsertWorkspaceNote } from '@nx-frontend/shared/api/webnotes';
import { clientMoveNote } from '@nx-frontend/shared/api/webnotes/client';
import { useOpenManagePopup } from '@nx-frontend/shared/hooks';
import { useParams } from '@nx-frontend/shared/hooks/router-proxy';
import { setAiOpenOptionsEvent } from '@nx-frontend/shared/store/ai-helper';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { setNoteFullScreenEvent } from '@nx-frontend/shared/store/note-fullscreen';
import { $organizations } from '@nx-frontend/shared/store/organizations';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { $userGranularPermissions } from '@nx-frontend/shared/store/user-granular-permissions';
import { $workspaceAccess, $wpCanManageMembers } from '@nx-frontend/shared/store/workspace-access';
import { $workspaces } from '@nx-frontend/shared/store/workspaces';
import { webnotesThemeColors, webnotesThemeStyle } from '@nx-frontend/shared/themes/webnotes';
import {
  AiMentionType,
  AppAccessDeniedError,
  ContextMenuItemData,
  MenuItem,
  MenuItemType,
  PopupType,
  SharePopupPayload,
  UiContextMenuConfigPosition,
  UiContextMenuTheme,
} from '@nx-frontend/shared/types';
import { copyToClipboard, getFilterMenuItemsByPrivileges } from '@nx-frontend/shared/utils';
import {
  contextItemLocMap,
  copyLinkItem,
  deleteItem,
  duplicateMenuItem,
  manageMembersItem,
  openInAiMenuItem,
  openInNewTabItem,
  renameMenuItem,
  shareItem,
  unshareItem,
} from './context-items';

export const useOpenKitchenMenuContextNote = () => {
  const [
    currentWorkspace,
    unleashFlags,
    userGranularPermissions,
    workspaceAccess,
    wpCanManageMembers,
    organizations,
    workspaces,
    currentOrganization,
  ] = useUnit([
    $currentWorkspace,
    $unleashFlags,
    $userGranularPermissions,
    $workspaceAccess,
    $wpCanManageMembers,
    $organizations,
    $workspaces,
    $currentOrganization,
  ]);
  const [openContextMenu, openModal, setAiOpenOptions, setNoteFullScreen, closeModal] = useEvent([
    openContextMenuEvent,
    openModalEvent,
    setAiOpenOptionsEvent,
    setNoteFullScreenEvent,
    closeModalEvent,
  ]);
  const { toast } = useToast();
  const router = useRouter();
  const params = useParams();
  const slug = useMemo(() => params?.slug || [], [params?.slug]) as string[];
  const openManagePopup = useOpenManagePopup();
  const unshareNote = useUnshareNote();
  const t = useTranslations();

  return useCallback(
    async (menuItem: MenuItem, e: MouseEvent, useMousePos?: boolean) => {
      const items: ContextMenuItemData[] = [];
      const workspaceId = currentWorkspace.globalId;
      const isShared = menuItem.isShared;
      const itemId =
        menuItem.type === MenuItemType.NOTE
          ? menuItem.id.replace('note#', '')
          : menuItem.id.replace('notesFolder#', '');

      const itemParentId = menuItem.parentId.replace('notesFolder#', '');

      const extendedShareItem = Object.assign({}, shareItem, {
        title: isShared ? t('sidebar.sidebar-context_menus.get_shared_link') : t('sidebar.sidebar-context_menus.share'),
        action: async () => {
          const sharePopupPayload: SharePopupPayload = {
            payloadType: PopupType.SHARE,
            globalId: itemId,
          };

          openModal({
            content: PopupType.SHARE,
            showCloseButton: false,
            payload: sharePopupPayload,
            usePadding: false,
          });
        },
      });

      const extendedRenameMenuItem = Object.assign({}, renameMenuItem, {
        title: renameMenuItem.id ? t(contextItemLocMap[renameMenuItem.id]) : renameMenuItem.title,
        action: () => {
          openModal({
            title: t('sidebar.sidebar-context_menus.rename'),
            content: PopupType.RENAME,
            showCloseButton: true,
            closeButtonSize: 18,
            payload: {
              type: 'note',
              value: menuItem.name,
              payloadType: PopupType.RENAME,
              themeColors: webnotesThemeColors,
              themeStyle: webnotesThemeStyle,
              onApply: (value: string) => {
                upsertWorkspaceNote({
                  workspaceId,
                  noteId: itemId,
                  note: {
                    title: value.trim(),
                  },
                });
              },
            },
          });
        },
      });

      const extendedOpenInAiMenuItem = Object.assign({}, openInAiMenuItem, {
        title: openInAiMenuItem.id ? t(contextItemLocMap[openInAiMenuItem.id]) : openInAiMenuItem.title,
        action: () => {
          trackChatOpen('sidebarMenu');
          setAiOpenOptions({ mention: { title: menuItem.name, type: AiMentionType.NOTE, globalId: itemId } });
        },
      });

      const extendedOpenInNewTabItem = Object.assign(
        {},
        openInNewTabItem,
        {
          isSeparator: true,
          title: openInNewTabItem.id ? t(contextItemLocMap[openInNewTabItem.id]) : openInNewTabItem.title,
        },
        {
          action: () => {
            window.open(`/space/${workspaceId}/page/${itemId}`, '_blank');
          },
        }
      );

      const extendedDuplicateMenuItem = Object.assign({}, duplicateMenuItem, {
        title: duplicateMenuItem.id ? t(contextItemLocMap[duplicateMenuItem.id]) : duplicateMenuItem.title,
        action: async () => {
          let isTaskRunned = false;
          let isErrorHandled = false;
          try {
            isTaskRunned = Boolean(
              await clientMoveNote({
                workspaceFrom: workspaceId,
                noteFrom: itemId,
                workspaceId,
                remove: false,
                parentId: itemParentId,
                titleSuffix: '(copy)',
                duplicating: true,
              })
            );
          } catch (err) {
            if (err instanceof AppAccessDeniedError) {
              isErrorHandled = true;
              showCopyAccessDeniedPopup(openModal);
            }
          }
          if (!isTaskRunned && !isErrorHandled) {
            toast({ title: t('sidebar.sidebar-duplicate_error') });
          }
        },
      });

      const extendedDeleteItem = Object.assign({}, deleteItem, {
        title: deleteItem.id ? t(contextItemLocMap[deleteItem.id]) : deleteItem.title,
        isSeparator: true,
        action: async () => {
          openModal({
            title: t('sidebar.sidebar-page_removal'),
            content: PopupType.DIALOG,
            showCloseButton: true,
            closeButtonSize: 18,
            payload: {
              payloadType: PopupType.DIALOG,
              text: t('sidebar.sidebar-are_you_sure_delete', { itemName: menuItem.name }),
              buttons: [
                {
                  text: t('sidebar.sidebar-cancel'),
                },
                {
                  text: t('sidebar.sidebar-context_menus.delete'),
                  main: true,
                  onClick: () => {
                    setNoteFullScreen && setNoteFullScreen(false);
                    const [, , globalId] = slug || [];
                    if (globalId === itemId) {
                      router.push('/space/' + workspaceId + '/folder/' + itemParentId);
                    }
                    upsertWorkspaceNote({
                      workspaceId,
                      noteId: itemId,
                      note: {
                        parentId: 'trash',
                        rootParentId: itemParentId,
                      },
                    });
                    closeModal();
                  },
                },
              ],
            },
          });
        },
      });

      const extendedManageMembersItem = Object.assign({}, manageMembersItem, {
        title: manageMembersItem.id ? t(contextItemLocMap[manageMembersItem.id]) : manageMembersItem.title,
        action: () => {
          const [, id] = menuItem.id.split('#');
          const [, parentId] = menuItem.parentId.split('#');
          openManagePopup({
            payloadType: PopupType.MANAGE,
            subjectType: 'note',
            globalId: id,
            parentId: parentId,
            title: menuItem.name,
            isDefaultOrInside: false,
          });
        },
      });

      // const items: ContextMenuItemData[] = [extendedShareItem];
      if (isShared) {
        const extendedUnshareItem = Object.assign({}, unshareItem, {
          title: unshareItem.id ? t(contextItemLocMap[unshareItem.id]) : unshareItem.title,
          action: () => {
            unshareNote({
              globalId: itemId,
              title: menuItem.name,
              workspaceId,
              type: MenuItemType.NOTES_FOLDER,
            });
          },
        });
        items.push(extendedUnshareItem);
      }

      const extendedCopyLinkItem = Object.assign({}, copyLinkItem, {
        title: copyLinkItem.id ? t(contextItemLocMap[copyLinkItem.id]) : copyLinkItem.title,
        action: () => {
          copyToClipboard(`${window.location.origin}/space/${workspaceId}/page/${itemId}`);
          toast({ title: t('sidebar.sidebar-copied') });
        },
      });

      items.push(
        ...[
          extendedShareItem,
          extendedCopyLinkItem,
          extendedOpenInNewTabItem,
          extendedRenameMenuItem,
          extendedOpenInAiMenuItem,
          extendedDuplicateMenuItem,
          extendedDeleteItem,
          extendedManageMembersItem,
        ]
      );
      if ((workspaces && workspaces.length > 1) || (organizations && organizations.length > 1)) {
        const moveToMenuItem = getMoveCopyMenuItem(
          {
            noteId: itemId,
            organizations,
            workspaces,
            currentOrgId: currentOrganization.globalId,
            currentWorkspaceId: workspaceId,
            toast,
            copy: false,
          },
          t
        );
        const copyToMenuItem = Object.assign(
          {},
          getMoveCopyMenuItem(
            {
              noteId: itemId,
              organizations,
              workspaces,
              currentOrgId: currentOrganization.globalId,
              currentWorkspaceId: workspaceId,
              toast,
              copy: true,
            },
            t
          ),
          { isSeparator: true }
        );
        const duplicateIndex = items.findIndex((item) => item.id === 'duplicate');
        if (duplicateIndex) {
          items.splice(duplicateIndex + 1, 0, moveToMenuItem, copyToMenuItem);
        } else {
          items.push(...[moveToMenuItem, copyToMenuItem]);
        }
      }
      openContextMenu({
        items: getFilterMenuItemsByPrivileges(
          wpCanManageMembers,
          workspaceAccess,
          userGranularPermissions,
          unleashFlags
        )(items),
        position: calculateContextMenuPosition(
          e.target,
          4,
          undefined,
          useMousePos ? { x: e.clientX, y: e.clientY } : undefined
        ),
        config: {
          position: UiContextMenuConfigPosition.BOTTOM_RIGHT,
          menuPadding: 0,
          theme: UiContextMenuTheme.LIGHT,
        },
      });
    },
    [
      closeModal,
      currentOrganization.globalId,
      currentWorkspace.globalId,
      openContextMenu,
      openManagePopup,
      openModal,
      unshareNote,
      organizations,
      router,
      setAiOpenOptions,
      setNoteFullScreen,
      slug,
      toast,
      unleashFlags,
      userGranularPermissions,
      workspaceAccess,
      workspaces,
      wpCanManageMembers,
      t,
    ]
  );
};
